import { jsx } from "react/jsx-runtime";
import { ContentType } from "../../../enums/contentStack.js";
import { findOneBusinessEntry, DEFAULT_FIELD_EXCEPTIONS, getConfigurationSettingByDeveloperKey } from "../../../services/content/business.lib.js";
import { ModuleSettingsStore } from "../../../utils/contentstack/moduleSettingsStore.js";
import { getLinkBehavior } from "../../../utils/targetLinkBehavior/getLinkBehavior.js";
import { SpaceLg } from "@nxt-costco-com/forge-design-tokens";
import { Analytics } from "../../Analytics/index.js";
import { AdSetCostco } from "../AdSetCostco/index.js";
import { ITEM_TYPE_CATEGORY, AD_SET_STYLE_ROTATING } from "../AdSetCostco/constants.js";
import { AdSetThirdParty } from "../AdSetThirdParty/index.js";
import { AddToListProvider } from "../../Product/AddToList/AddToListProvider/index.js";
import { AsyncComponentWrapper } from "../../Scaffolding/AsyncComponentWrapper/index.js";
import { CostcoBox } from "../../Util/CostcoBox.js";
import { MODULE_SETTING_DEVELOPER_KEY, LINK_LABEL_KEY, COMPONENT_NAME } from "./constants.js";
const getEntryWrapperStyleModifications = (contentType, entry) => {
  const isEntryFourCorners = entry.item_type === ITEM_TYPE_CATEGORY && entry.ad_set_style !== AD_SET_STYLE_ROTATING;
  if (contentType === "ad_set_costco" && entry.item_type === ITEM_TYPE_CATEGORY) {
    return {
      alignItems: null,
      ...isEntryFourCorners ? { minHeight: "100%" } : {}
    };
  }
};
async function AdSet({
  addToIndex = false,
  contentType,
  entryId,
  enableLivePreview = false,
  locale,
  isSearchPage = false,
  isCategoryPage = false,
  isProductDetailsPage = false,
  maxSlides,
  maxSlidesBySize,
  productTileSx,
  carouselContainerSx,
  sx,
  contextData,
  analyticsTitlePrefix
}) {
  if (!(contentType === "ad_set_3rd_party" || contentType === "ad_set_costco")) {
    throw Error(
      `Unsupported content type provided to AdSet: ${contentType}.`
    );
  }
  let entry = null;
  try {
    entry = await findOneBusinessEntry(
      contentType,
      entryId,
      locale,
      DEFAULT_FIELD_EXCEPTIONS,
      enableLivePreview
    );
  } catch {
    console.error(
      `Missing AdSet entry in ContentStack: ${entryId}. Skipping...`
    );
    return null;
  }
  if (!(entry == null ? void 0 : entry.ad_set_heading_and_costco_url)) {
    return null;
  }
  const moduleSettings = await getConfigurationSettingByDeveloperKey(
    ContentType.ModuleSettings,
    MODULE_SETTING_DEVELOPER_KEY,
    locale,
    DEFAULT_FIELD_EXCEPTIONS,
    enableLivePreview
  );
  if (!moduleSettings) {
    console.error(`Module settings not found.`);
    return null;
  }
  const csStore = new ModuleSettingsStore({
    composable_window: [],
    key_value: moduleSettings.key_value
  });
  const { title, url, external_site, same_tab_domain } = entry.ad_set_heading_and_costco_url;
  const { target } = getLinkBehavior(external_site, same_tab_domain);
  const titleProps = {
    href: url,
    isExternalSite: external_site,
    linkLabel: csStore.getKeyValueItem(LINK_LABEL_KEY),
    title,
    titleId: `${entry.uid}-titleId`,
    target
  };
  const itemCuration = contentType === "ad_set_3rd_party" ? entry.item_curation : "costco";
  const AdComponent = contentType === "ad_set_3rd_party" ? /* @__PURE__ */ jsx(
    AdSetThirdParty,
    {
      carouselContainerSx,
      contextData: contextData || {},
      entry,
      isCategoryPage,
      isProductDetailsPage,
      isSearchPage,
      locale,
      maxSlides,
      maxSlidesBySize,
      productTileSx,
      sx,
      titleProps
    }
  ) : await AsyncComponentWrapper({
    Component: AdSetCostco,
    componentProps: {
      addToIndex,
      entry,
      locale,
      sx,
      titleProps,
      enableLivePreview,
      maxSlidesBySize,
      itemCuration
    },
    shouldAwait: addToIndex
  });
  let analyticsTitle = entry.title;
  if (analyticsTitlePrefix) {
    analyticsTitle = `${analyticsTitlePrefix} | ${analyticsTitle}`;
  }
  const analyticsData = contentType === "ad_set_3rd_party" ? {
    adItem: {
      title,
      type: entry.item_type
    },
    itemCuration,
    title: analyticsTitle
  } : {
    adItem: {
      type: entry.item_type
    },
    itemCuration,
    title: analyticsTitle
  };
  const wrapperStyleModifications = getEntryWrapperStyleModifications(contentType, entry) || {};
  return /* @__PURE__ */ jsx(
    CostcoBox,
    {
      sx: {
        display: "flex",
        flexWrap: "wrap",
        gap: SpaceLg,
        justifyContent: "center",
        width: "100%",
        ...wrapperStyleModifications
      },
      "data-testid": `${COMPONENT_NAME}-all-${entry.title.toLowerCase().replace(/ /g, "_")}`,
      children: /* @__PURE__ */ jsx(AddToListProvider, { locale, children: /* @__PURE__ */ jsx(Analytics, { analyticData: analyticsData, children: AdComponent }) })
    }
  );
}
AdSet.displayName = COMPONENT_NAME;
export {
  AdSet
};
